<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DatePicker from 'vue-datepicker-next';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
  initialContent: {
    type: String,
    default: '',
  },
  // Quando presente, o modal está editando uma mensagem agendada existente
  scheduledMessage: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const { t } = useI18n();

const content = ref('');
const scheduledAt = ref(null);

const lang = {
  days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  yearFormat: 'YYYY',
  monthFormat: 'MMMM',
};

const isEditing = computed(() => !!props.scheduledMessage);

const uiFlags = computed(() => store.getters['scheduledMessages/getUIFlags']);
const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const isValid = computed(() => !!content.value.trim() && !!scheduledAt.value);

// Repopula o form sempre que o modal abre
watch(
  () => props.show,
  isShown => {
    if (!isShown) return;
    if (props.scheduledMessage) {
      content.value = props.scheduledMessage.content || '';
      scheduledAt.value = props.scheduledMessage.scheduled_at
        ? new Date(props.scheduledMessage.scheduled_at)
        : null;
    } else {
      content.value = props.initialContent || '';
      scheduledAt.value = null;
    }
  },
  { immediate: true }
);

const disabledDate = date => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date < yesterday;
};

const disabledTime = date => date < new Date();

const onClose = () => emit('close');

const onSubmit = async () => {
  if (!isValid.value) return;
  try {
    const payload = {
      conversationId: props.conversationId,
      content: content.value.trim(),
      scheduledAt: scheduledAt.value.toISOString(),
    };
    if (isEditing.value) {
      await store.dispatch('scheduledMessages/update', {
        ...payload,
        scheduledMessageId: props.scheduledMessage.id,
      });
    } else {
      await store.dispatch('scheduledMessages/create', payload);
    }
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.SAVED'));
    emit('saved');
    emit('close');
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.ERROR'));
  }
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose" @close="onClose">
    <div class="flex flex-col">
      <woot-modal-header
        :header-title="
          isEditing
            ? $t('CONVERSATION.SCHEDULE_MESSAGE.EDIT_TITLE')
            : $t('CONVERSATION.SCHEDULE_MESSAGE.TITLE')
        "
      />
      <form
        class="modal-content w-full pt-2 px-5 pb-6 flex flex-col gap-4"
        @submit.prevent="onSubmit"
      >
        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.SCHEDULE_MESSAGE.MESSAGE_LABEL') }}
          <textarea
            v-model="content"
            rows="3"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 p-2 text-sm text-n-slate-12"
            :placeholder="
              $t('CONVERSATION.SCHEDULE_MESSAGE.MESSAGE_PLACEHOLDER')
            "
          />
        </label>

        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.SCHEDULE_MESSAGE.DATETIME_LABEL') }}
          <DatePicker
            v-model:value="scheduledAt"
            type="datetime"
            input-class="mx-input"
            :lang="lang"
            :disabled-date="disabledDate"
            :disabled-time="disabledTime"
            :placeholder="
              $t('CONVERSATION.SCHEDULE_MESSAGE.DATETIME_PLACEHOLDER')
            "
          />
        </label>

        <div class="flex flex-row justify-end w-full gap-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :is-loading="isSaving"
            :disabled="!isValid || isSaving"
            :label="
              isEditing
                ? $t('CONVERSATION.SCHEDULE_MESSAGE.UPDATE')
                : $t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULE')
            "
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
